import React, { FunctionComponent, useMemo } from 'react'

import { PROPERTY_TYPES } from '../../../common/drmTypes'
import { TABLE_VIEW } from '../../../common/foreclosureMonitoringTypes'
import { MonitoringInsolvenciFetchResult } from '../../../common/insolvencyMonitoringTypes'
import { defaultViewParam } from '../../utils/paths'
import { monitoringViewSlug } from '../../utils/tabsHelpers'
import { useUserActivityLogCheck } from '../../utils/useCheckUserActivityLog'
import { usePropertiesLabels } from '../../utils/usePropertiesLabels'
import { usePropertyTableNoteCheck } from '../../utils/usePropertyTableNoteCheck'
import { useTabKeyState } from '../../utils/useTabKeyState'
import { MonitoringContentSuspense } from '../MonitoringContentSuspense/MonitoringContentSuspense'
import { PropertyMonitoringFetchError } from '../PropertyMonitoringFetchError/PropertyMonitoringFetchError'

const InsolvencyMonitoringTable = React.lazy(
  () => import('../InsolvencyMonitoringTable/InsolvencyMonitoringTable')
)
const InsolvencyMonitoringTiles = React.lazy(
  () => import('../InsolvencyMonitoringTiles/InsolvencyMonitoringTiles')
)
const InsolvencyMonitoringCompactTable = React.lazy(
  () =>
    import(
      '../InsolvencyMonitoringCompactTable/InsolvencyMonitoringCompactTable'
    )
)

export interface InsolvencyMonitoringContentProps {
  propertyType: PROPERTY_TYPES
  dataSource?: MonitoringInsolvenciFetchResult
}

export const InsolvencyMonitoringContent: FunctionComponent<InsolvencyMonitoringContentProps> =
  props => {
    const [viewType] = useTabKeyState<TABLE_VIEW>(
      TABLE_VIEW.TABLE,
      monitoringViewSlug,
      defaultViewParam
    )

    const propertyIds = useMemo(
      () => props.dataSource?.data?.data?.map(item => item.id) || [],
      [props.dataSource]
    )
    const userActivityLogs = useUserActivityLogCheck(propertyIds)
    const propertyNotes = usePropertyTableNoteCheck(propertyIds)
    const { propertyLabels, loading: labelsLoading } = usePropertiesLabels(
      propertyIds,
      props.propertyType
    )

    if (props.dataSource?.error)
      return <PropertyMonitoringFetchError type="insolvencí" />

    return (
      <MonitoringContentSuspense>
        {viewType === TABLE_VIEW.TILES && (
          <InsolvencyMonitoringTiles
            data={props.dataSource?.data?.data}
            propertyType={props.propertyType}
            propertiesActivity={userActivityLogs}
            propertyNotes={propertyNotes}
            propertyLabels={labelsLoading ? null : propertyLabels}
          />
        )}
        {viewType === TABLE_VIEW.TABLE && (
          <InsolvencyMonitoringTable
            data={props.dataSource?.data?.data}
            propertyType={props.propertyType}
            userActivityLogs={userActivityLogs}
            propertyNotes={propertyNotes}
            propertyLabels={labelsLoading ? 'loading' : propertyLabels}
          />
        )}
        {viewType === TABLE_VIEW.COMPACT_TABLE && (
          <InsolvencyMonitoringCompactTable
            data={props.dataSource?.data?.data}
            propertyType={props.propertyType}
            propertiesActivity={userActivityLogs}
            propertyNotes={propertyNotes}
            propertyLabels={labelsLoading ? null : propertyLabels}
          />
        )}
      </MonitoringContentSuspense>
    )
  }
